import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addPersonToProject, archivePerson, createPerson, fetchPeople, giveLogin, removePersonFromProject,
  restorePerson, updatePerson,
} from '../../services/PeopleService';
import type { UserRole } from '../../types/User';
import type { PeopleStatus, Person, PersonInput } from '../../types/People';

export interface PeopleData {
  people: Person[];
  loading: boolean;
  /** The list failed to load. Shown instead of an empty list. */
  loadError: unknown;
  refresh: () => Promise<void>;
  /** Each change reloads the list when it goes through, and throws when it does not. */
  add: (input: PersonInput) => Promise<void>;
  /** Saves the e-mail first when one is given, then creates the login. */
  createLogin: (personId: string, role: UserRole, email?: string) => Promise<Person>;
  addToProject: (personId: string, projectId: string, teams: string[]) => Promise<void>;
  removeFromProject: (personId: string, projectId: string) => Promise<void>;
  archive: (personId: string) => Promise<void>;
  restore: (personId: string) => Promise<void>;
}

/**
 * Loads one list of the workspace's people (active or archived) and offers the
 * changes the Team and contacts screen makes. A late answer for a list that is
 * no longer on screen is dropped.
 */
export function usePeople(status: PeopleStatus, enabled = true): PeopleData {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [loadError, setLoadError] = useState<unknown>(null);
  const ticket = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    const mine = ++ticket.current;
    setLoading(true);
    try {
      const rows = await fetchPeople(status);
      if (mine !== ticket.current) return;
      setPeople(rows);
      setLoadError(null);
    } catch (err) {
      if (mine !== ticket.current) return;
      setLoadError(err ?? new Error('load failed'));
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [status, enabled]);

  useEffect(() => {
    setPeople([]);
    void refresh();
    return () => { ticket.current += 1; };
  }, [refresh]);

  const then = useCallback((change: Promise<unknown>) => change.then(() => refresh()), [refresh]);

  return {
    people, loading, loadError, refresh,
    add: (input) => then(createPerson(input)),
    createLogin: async (personId, role, email) => {
      try {
        if (email) await updatePerson(personId, { email });
        return await giveLogin(personId, role);
      } finally {
        // Also after a partial failure (e-mail saved, login not), so the list is not stale.
        await refresh();
      }
    },
    addToProject: (personId, projectId, teams) => then(addPersonToProject(personId, projectId, teams)),
    removeFromProject: (personId, projectId) => then(removePersonFromProject(personId, projectId)),
    archive: (personId) => then(archivePerson(personId)),
    restore: (personId) => then(restorePerson(personId)),
  };
}
